import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import React, { CSSProperties, useCallback, useRef, useState } from "react";
import { fontColor, FONTS } from "../../styles/fonts";
import cn from "classnames";
import { CloseButtonSvg } from "../../assets";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { useClickOutside } from "../../hooks/use-click-outside";
import { CSS_MQ } from "../../styles/breakpoints";
import { ThreeDModelComponentType } from "@swo/storefront";

export type ConfigurationOption = NonNullable<ThreeDModelComponentType["variants"]>[0];

export const ConfigurationOptions: ReactTFC<{
  options: Array<ConfigurationOption>;
  selectedOption: ConfigurationOption;
  onOptionSelected: (opt: ConfigurationOption) => void;
  translations: ThreeDModelComponentType["translations"];
}> = props => {
  const [isOpened, setOpened] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const cb = useCallback(() => {
    setOpened(false);
  }, []);
  useClickOutside(ref, cb, true);

  return (
    <div
      ref={ref}
      css={css`
        position: absolute;
        z-index: 3;

        ${CSS_MQ.atMostSm} {
          right: 20px;
          bottom: 60px;

          max-width: 80px;

          --s: 32px;
        }

        ${CSS_MQ.atLeastMd} {
          left: 10%;
          bottom: 10%;

          max-width: 100px;

          --s: 40px;
        }

        --g: 8px;
        ${CSS_MQ.atLeastMd} {
          --g: 20px;
        }

        --w: 6px;
        --bs: 1px;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;

          background-color: ${CSS_COLORS.White};
          box-shadow: 0px 0px 10px 4px #00000040;

          ${CSS_MQ.atMostSm} {
            padding: 4px 6px;
            padding-bottom: 10px;

            border-radius: 32px;
          }

          ${CSS_MQ.atLeastMd} {
            border-radius: 40px;

            padding: 8px 12px;
            padding-bottom: 20px;

            min-width: 100px;
          }
        `}
      >
        <span
          css={[
            FONTS.SwarovskiFutura500,
            fontColor.black,
            css`
              margin-top: 20px;
              margin-bottom: 14px;

              text-transform: uppercase;

              font-size: 12px;
              line-height: 1.2;
              text-overflow: ellipsis;
              overflow: hidden;
              max-height: 2.4em;

              text-align: center;
            `,
          ]}
        >
          {props.translations.configuration.headline}
        </span>

        {isOpened ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <ul
              css={css`
                margin-bottom: var(--g);

                display: flex;
                flex-direction: column;

                gap: var(--g);
              `}
            >
              {props.options.map(opt => {
                return (
                  <li
                    key={opt.colorHexCode}
                    css={css`
                      display: flex;
                      justify-content: center;
                    `}
                  >
                    <button onClick={() => props.onOptionSelected(opt)}>
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                        `}
                      >
                        <div
                          className={cn({ "with-border": opt.textureUrl === props.selectedOption.textureUrl })}
                          css={[
                            css`
                              width: var(--s);
                              height: var(--s);
                              border-radius: 100%;

                              background-color: var(--c);
                            `,
                            borderStyles,
                            effectOnHover,
                          ]}
                          style={{ "--c": opt.colorHexCode } as CSSProperties}
                        />

                        <span css={labelStyles}>{opt.label}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="u-relative u-flex u-flex-col u-jc-center u-ai-center">
              <button
                onClick={() => setOpened(false)}
                css={css`
                  --v: calc(var(--s) - var(--w));
                  width: var(--v);

                  &:hover {
                    ._wrapper {
                      color: ${CSS_COLORS.White};
                    }
                    ._wrapper::before {
                      background-color: black;
                      color: ${CSS_COLORS.White};
                    }
                  }

                  ._wrapper {
                    height: var(--v);
                    width: var(--v);

                    color: black;

                    svg {
                      height: 100%;
                      width: 100%;

                      padding: 6px;

                      color: inherit;
                      position: relative;
                    }
                  }
                `}
              >
                <div className={"_wrapper with-border"} css={borderStyles}>
                  <CloseButtonSvg />
                </div>
              </button>

              <span
                css={[
                  labelStyles,
                  css`
                    max-width: 5em;

                    ${CSS_MQ.atLeastMd} {
                      max-width: 6em;
                    }
                  `,
                ]}
              >
                {props.translations.configuration.close}
              </span>
            </div>
          </div>
        ) : (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;

              margin-bottom: 9px; // center with close button
              ${CSS_MQ.atLeastMd} {
                margin-bottom: 13px; // center with close button
              }
            `}
          >
            <button onClick={() => setOpened(true)}>
              <ColorCircle currentColorCSS={props.selectedOption.colorHexCode} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const labelStyles = [
  FONTS.SwarovskiFutura500,
  css`
    text-transform: uppercase;

    font-size: 12px;
    margin-top: 6px;

    ${fontColor.black};
    text-align: center;

    text-overflow: ellipsis;
    overflow: hidden;
    max-height: 2.2em;
    line-height: 1.2;
  `,
];

const borderStyles = css`
  position: relative;

  &.with-border::before {
    position: absolute;
    content: "";

    left: calc(-0.5 * var(--w) - var(--bs));
    top: calc(-0.5 * var(--w) - var(--bs));

    width: calc(100% + var(--w));
    height: calc(100% + var(--w));

    border-style: solid;
    border-color: ${CSS_COLORS.GreenDark};
    border-width: var(--bs);
    border-radius: 100%;
  }
`;

const effectOnHover = css`
  transition: all 0.2s ease-out;
  &:hover {
    transition: all 0.2s ease-out;
    filter: brightness(1.15);
  }
`;

const ColorCircle: ReactTFC<{ currentColorCSS: string }> = props => {
  const backgroundCss = props.currentColorCSS;

  return (
    <div
      css={[
        css`
          --x: calc(var(--s) + 10px);
          width: var(--x);
          height: var(--x);

          border-radius: 100%;
        `,
        effectOnHover,
      ]}
      style={{
        background: backgroundCss,
      }}
    />
  );
};
